import { FieldNamesMarkedBoolean, FieldValues } from 'react-hook-form'

type CustomMapper<TForm> = {
  [Key in keyof TForm]?: (value: TForm[Key]) => unknown
}

type MappedResult<TForm, TMapper extends CustomMapper<TForm>> = {
  [Key in keyof TForm]: Key extends keyof TMapper
    ? TMapper[Key] extends (...args: any[]) => infer TResult
      ? TResult
      : TForm[Key]
    : TForm[Key]
}

interface ExtractDirtyFormValuesParams<
  TForm extends FieldValues,
  TMapper extends CustomMapper<TForm>,
> {
  dirtyFields: Partial<FieldNamesMarkedBoolean<TForm>>
  values: TForm
  customMappers?: TMapper
  fieldsToOmit?: (keyof TForm)[]
}

export const extractDirtyFormValues = <
  TForm extends FieldValues,
  TMapper extends CustomMapper<TForm> = {},
>({
  dirtyFields,
  values,
  customMappers,
  fieldsToOmit = [],
}: ExtractDirtyFormValuesParams<TForm, TMapper>): Partial<
  MappedResult<TForm, TMapper>
> => {
  const result: Partial<MappedResult<TForm, TMapper>> = {}

  for (const key in dirtyFields) {
    if (dirtyFields[key] && !fieldsToOmit.includes(key as keyof TForm)) {
      const typedKey = key as keyof TForm
      const value = values[typedKey]

      if (value !== undefined) {
        if (customMappers?.[typedKey]) {
          result[typedKey as keyof MappedResult<TForm, TMapper>] =
            customMappers[typedKey]!(value) as MappedResult<
              TForm,
              TMapper
            >[typeof typedKey]
        } else {
          result[typedKey as keyof MappedResult<TForm, TMapper>] =
            value as MappedResult<TForm, TMapper>[typeof typedKey]
        }
      }
    }
  }

  return result
}
